import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/wms/api')

export type OutboundStatus =
  | 'draft'
  | 'released'
  | 'picking'
  | 'picked'
  | 'shipped'
  | 'closed'
  | 'cancelled'

export type OutboundSource = 'manual' | 'import'

export interface Order {
  id: number
  tenant_id: number
  doc_no: string
  warehouse_id: number
  customer_id: number
  expect_ship_at: string | null
  status: OutboundStatus
  source: OutboundSource
  wave_id: number
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface OrderItem {
  id: number
  tenant_id: number
  order_id: number
  sku_id: number
  qty: number
  picked_qty: number
  created_at: string
  updated_at: string
}

/** 列表行：单据 + 客户/仓库名 + 应发/已拣汇总，一次请求省掉三次回查 */
export interface ListRow extends Order {
  customer_name: string
  warehouse_name: string
  total_qty: number
  picked_qty: number
}

export interface ItemDetail extends OrderItem {
  sku_code: string
  sku_name: string
  unit: string
}

export interface Detail {
  order: Order
  items: ItemDetail[]
  warehouse_name: string
  customer_name: string
}

export interface ItemInput {
  sku_id: number
  qty: number
}

export interface OrderInput {
  warehouse_id: number
  customer_id: number
  /** 期望发货日期，YYYY-MM-DD HH:mm:ss；不填传 null */
  expect_ship_at: string | null
  remark: string
  items: ItemInput[]
}

export interface OutboundQuery extends PageQuery {
  keyword?: string
  warehouse?: number
  customer?: number
  status?: string
  source?: string
}

/** CSV 导入结果：成功张数 + 逐行错误（行号含表头，第一行数据是 2） */
export interface LineError {
  line: number
  msg: string
}

export interface ImportResult {
  created: number
  errors: LineError[]
}

/** 与后端 services/wms/internal/outbound/model.go 的字典保持一致，改一边必须改另一边 */
export const STATUS_LABELS: Record<OutboundStatus, string> = {
  draft: '草稿',
  released: '已释放',
  picking: '拣货中',
  picked: '已拣货',
  shipped: '已发货',
  closed: '已关闭',
  cancelled: '已取消',
}

export const SOURCE_LABELS: Record<OutboundSource, string> = {
  manual: '手工',
  import: 'CSV 导入',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<
  OutboundStatus,
  'info' | 'warning' | 'primary' | 'success' | 'danger'
> = {
  draft: 'info',
  released: 'primary',
  picking: 'warning',
  picked: 'success',
  shipped: 'success',
  closed: 'info',
  cancelled: 'danger',
}

export const listOrders = (q: OutboundQuery): Promise<PageResult<ListRow>> =>
  api.get<PageResult<ListRow>>('/outbound-orders', { ...q })

export const getOrder = (id: number): Promise<Detail> => api.get<Detail>(`/outbound-orders/${id}`)

export const createOrder = (input: OrderInput): Promise<Order> =>
  api.post<Order>('/outbound-orders', input)

export const updateOrder = (id: number, input: OrderInput): Promise<Order> =>
  api.put<Order>(`/outbound-orders/${id}`, input)

export const deleteOrder = (id: number): Promise<void> => api.del<void>(`/outbound-orders/${id}`)

export const releaseOrder = (id: number): Promise<void> =>
  api.post<void>(`/outbound-orders/${id}/release`, {})

export const cancelOrder = (id: number): Promise<void> =>
  api.post<void>(`/outbound-orders/${id}/cancel`, {})

export const closeOrder = (id: number): Promise<void> =>
  api.post<void>(`/outbound-orders/${id}/close`, {})

export const shipOrder = (id: number): Promise<Order> =>
  api.post<Order>(`/outbound-orders/${id}/ship`, {})

/** 下载导入模板：带 BOM 的 CSV，前端只管存盘，不再二次加工 */
export const importTemplate = (): Promise<Blob> => api.getBlob('/outbound-orders/import-template')

/** 把 CSV 正文原样发上去；全部行失败时后端回 400，Data 里仍带逐行错误 */
export const importOrders = (csv: string): Promise<ImportResult> =>
  api.postText<ImportResult>('/outbound-orders/import', csv)
